import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import PageHead from "@/components/PageHead";

type Flag = {
  id: string;
  key: string;
  description: string | null;
  enabled_global: boolean;
};

export default function AdminFlags() {
  const [flags, setFlags] = useState<Flag[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data, error } = await (supabase.from as any)("feature_flags")
      .select("id,key,description,enabled_global")
      .order("key");
    if (error) toast.error(error.message);
    setFlags((data as Flag[]) ?? []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const toggle = async (flag: Flag, next: boolean) => {
    setFlags((prev) => prev.map((f) => (f.id === flag.id ? { ...f, enabled_global: next } : f)));
    const { error } = await (supabase.from as any)("feature_flags")
      .update({ enabled_global: next })
      .eq("id", flag.id);
    if (error) {
      toast.error(error.message);
      load();
    } else {
      toast.success(`${flag.key} ${next ? "enabled" : "disabled"}`);
    }
  };

  return (
    <>
      <PageHead title="Feature Flags | Admin" description="Toggle platform features" path="/admin/flags" />
      <div className="container max-w-3xl py-10">
        <h1 className="text-3xl font-bold mb-2">Feature Flags</h1>
        <p className="text-muted-foreground mb-6">
          Enable or disable features globally. Per-org and per-user overrides live in the database.
        </p>
        {loading ? (
          <div className="text-muted-foreground">Loading…</div>
        ) : (
          <div className="space-y-3">
            {flags.map((f) => (
              <Card key={f.id}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-base font-mono">{f.key}</CardTitle>
                  <Switch
                    checked={f.enabled_global}
                    onCheckedChange={(v) => toggle(f, v)}
                  />
                </CardHeader>
                {f.description && (
                  <CardContent className="text-sm text-muted-foreground pt-0">
                    {f.description}
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
